// @ts-nocheck
/* eslint-disable */
import * as React from 'react';
const { useState } = React;
const h = React.createElement;
import { icons } from '../icons';
import { Empty } from './Empty';
import { ActionButton } from './ActionButton';

// ========== Table ==========
const Table = function Table(props) {
  const {
    columns = [],
    dataSource = [],
    rowKey = 'key',
    loading = false,
    pagination,
    scroll,
    size = 'default',
    rowSelection,
    onRow,
    locale,
    className = '',
    onChange,
  } = props;

  const [innerSortField, setInnerSortField] = useState(null);
  const [innerSortOrder, setInnerSortOrder] = useState(null);
  const [innerPage, setInnerPage] = useState(pagination?.current || 1);
  const [innerPageSize, setInnerPageSize] = useState(pagination?.pageSize || 10);

  // Controlled vs uncontrolled sorting:
  // If any column has sortOrder prop, treat as controlled mode (like Ant Design API).
  const isControlledSort = columns.some((col) => col.sortOrder !== undefined);

  // In controlled mode, derive sort state from column sortOrder props; otherwise use internal state.
  let currentSortField, currentSortOrder;
  if (isControlledSort) {
    const sortedCol = columns.find((col) => col.sortOrder);
    currentSortField = sortedCol
      ? sortedCol.columnKey || sortedCol.key || sortedCol.dataIndex
      : null;
    currentSortOrder = sortedCol ? sortedCol.sortOrder : null;
  } else {
    currentSortField = innerSortField;
    currentSortOrder = innerSortOrder;
  }

  // Controlled vs uncontrolled pagination:
  // If caller passes pagination.current / pagination.pageSize, treat them as controlled values.
  const currentPage = pagination && pagination.current != null ? pagination.current : innerPage;
  const currentPageSize =
    pagination && pagination.pageSize != null ? pagination.pageSize : innerPageSize;

  // Server-side pagination detection:
  // If caller provides pagination.total that is larger than dataSource.length, the caller has
  // already sliced the data for the current page. In that case we must NOT slice again.
  const serverSidePaging =
    pagination &&
    pagination !== false &&
    typeof pagination.total === 'number' &&
    pagination.total > dataSource.length;

  // Sort data (only in uncontrolled mode; controlled mode expects caller to pre-sort)
  let sortedData = dataSource;
  if (!isControlledSort && currentSortField && !serverSidePaging) {
    sortedData = [].concat(dataSource).sort((a, b) => {
      const aVal = a[currentSortField];
      const bVal = b[currentSortField];
      if (aVal < bVal) return currentSortOrder === 'ascend' ? -1 : 1;
      if (aVal > bVal) return currentSortOrder === 'ascend' ? 1 : -1;
      return 0;
    });
  }

  // Paginate data if pagination is provided
  let paginatedData = sortedData;
  let total = dataSource.length;
  if (pagination !== false && pagination) {
    total = pagination.total != null ? pagination.total : dataSource.length;
    if (serverSidePaging) {
      // Data is already the current page; use as-is.
      paginatedData = sortedData;
    } else {
      const start = (currentPage - 1) * currentPageSize;
      paginatedData = sortedData.slice(start, start + currentPageSize);
    }
  }

  // Build current sorter object for onChange callbacks
  const buildSorter = () => {
    if (!currentSortField) return {};
    // Find the column with matching key to get the dataIndex
    const sortedCol = columns.find(
      (c) => (c.columnKey || c.key || c.dataIndex) === currentSortField,
    );
    return {
      field: sortedCol ? sortedCol.dataIndex : currentSortField,
      columnKey: currentSortField,
      order: currentSortOrder,
    };
  };

  const handleSort = (col) => {
    if (!col.sorter) return;
    const colKey = col.columnKey || col.key || col.dataIndex;

    // Three-state cycle: ascend → descend → null (clear sort)
    let newOrder;
    if (currentSortField === colKey) {
      if (currentSortOrder === 'ascend') {
        newOrder = 'descend';
      } else if (currentSortOrder === 'descend') {
        newOrder = null;
      } else {
        newOrder = 'ascend';
      }
    } else {
      newOrder = 'ascend';
    }

    if (isControlledSort) {
      // Controlled mode: notify parent via onChange
      if (onChange) {
        onChange(
          { current: currentPage, pageSize: currentPageSize },
          {},
          newOrder ? { field: col.dataIndex, columnKey: colKey, order: newOrder } : {},
        );
      }
    } else {
      // Uncontrolled mode: update internal state
      if (newOrder) {
        setInnerSortField(colKey);
        setInnerSortOrder(newOrder);
      } else {
        setInnerSortField(null);
        setInnerSortOrder(null);
      }
    }
  };

  const handlePageChange = (page, pageSize) => {
    setInnerPage(page);
    setInnerPageSize(pageSize);
    if (pagination?.onChange) pagination.onChange(page, pageSize);
    // Notify Table.onChange so parent can update controlled pagination state
    if (onChange) {
      onChange({ current: page, pageSize }, {}, buildSorter());
    }
  };

  const cellPadding = size === 'small' ? 'px-3 py-2' : 'px-4 py-3';
  const selectedKeys = rowSelection?.selectedRowKeys || [];

  const renderPagination = () => {
    if (pagination === false || !pagination) return null;
    const totalPages = Math.ceil(total / currentPageSize);
    const showTotal = pagination.showTotal;
    const showSizeChanger = pagination.showSizeChanger;

    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return h(
      'div',
      { className: 'flex items-center justify-between px-4 py-3 border-t border-dark-700' },
      h(
        'div',
        { className: 'text-sm text-dark-400' },
        showTotal ? showTotal(total) : `Total ${total} items`,
      ),
      h(
        'div',
        { className: 'flex items-center gap-1' },
        showSizeChanger &&
          h(
            'select',
            {
              className:
                'bg-dark-800 border border-dark-700 rounded text-sm text-dark-300 px-2 py-1 mr-2',
              value: currentPageSize,
              onChange: (e) => {
                const newSize = Number(e.target.value);
                setInnerPageSize(newSize);
                setInnerPage(1);
                if (pagination?.onChange) pagination.onChange(1, newSize);
                // Notify Table.onChange so parent can update controlled pagination state
                if (onChange) {
                  onChange({ current: 1, pageSize: newSize }, {}, buildSorter());
                }
              },
            },
            (pagination.pageSizeOptions || ['10', '20', '50', '100']).map((s) =>
              h('option', { key: s, value: Number(s) }, `${s} / page`),
            ),
          ),
        // 分页器统一走 ActionButton，与工具栏/表格行内按钮同尺寸同风格
        h(ActionButton, {
          action: 'prev',
          iconOnly: true,
          disabled: currentPage === 1,
          onClick: () => handlePageChange(currentPage - 1, currentPageSize),
        }),
        pageNumbers.map((num) =>
          h(
            ActionButton,
            {
              key: num,
              action: 'page',
              variant: num === currentPage ? 'primary' : 'default',
              onClick: () => handlePageChange(num, currentPageSize),
            },
            num,
          ),
        ),
        h(ActionButton, {
          action: 'next',
          iconOnly: true,
          disabled: currentPage === totalPages,
          onClick: () => handlePageChange(currentPage + 1, currentPageSize),
        }),
      ),
    );
  };

  return h(
    'div',
    { className: `overflow-hidden rounded-xl border border-dark-700 ${className}` },
    loading &&
      h(
        'div',
        { className: 'absolute inset-0 bg-dark-900/50 z-10 flex items-center justify-center' },
        h(
          'svg',
          {
            className: 'animate-spin h-8 w-8 text-primary-500',
            fill: 'none',
            viewBox: '0 0 24 24',
          },
          h('circle', {
            className: 'opacity-25',
            cx: '12',
            cy: '12',
            r: '10',
            stroke: 'currentColor',
            strokeWidth: '4',
          }),
          h('path', {
            className: 'opacity-75',
            fill: 'currentColor',
            d: 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z',
          }),
        ),
      ),
    h(
      'div',
      { className: 'overflow-x-auto', style: scroll?.x ? { maxWidth: '100%' } : undefined },
      h(
        'table',
        { className: 'w-full text-sm' },
        h(
          'thead',
          null,
          h(
            'tr',
            { className: 'bg-dark-800/50' },
            rowSelection &&
              h(
                'th',
                { className: `${cellPadding} text-left w-10` },
                h('input', {
                  type: 'checkbox',
                  className:
                    'rounded border-dark-600 bg-dark-800 text-primary-600 focus:ring-primary-500',
                  checked: selectedKeys.length > 0 && selectedKeys.length === paginatedData.length,
                  onChange: (e) => {
                    if (rowSelection.onChange) {
                      rowSelection.onChange(
                        e.target.checked ? paginatedData.map((r) => r[rowKey]) : [],
                      );
                    }
                  },
                }),
              ),
            columns.map((col, i) =>
              h(
                'th',
                {
                  key: col.key || col.dataIndex || i,
                  // overflow-hidden + text-ellipsis：表头是 whitespace-nowrap，
                  // 列宽不足时文字会直接溢出并压到相邻列上（如「Terminal Version」
                  // 覆盖 Status）。截断显示省略号，保证表头各列互不侵占。
                  className: `${cellPadding} text-left font-medium text-dark-300 whitespace-nowrap overflow-hidden text-ellipsis ${col.sorter ? 'cursor-pointer select-none hover:text-dark-100' : ''}`,
                  style: col.width ? { width: col.width, maxWidth: col.width } : undefined,
                  onClick: () => handleSort(col),
                },
                col.title,
                col.sorter &&
                  currentSortField === (col.columnKey || col.key || col.dataIndex) &&
                  currentSortOrder &&
                  h(
                    'span',
                    { className: 'ml-1' },
                    currentSortOrder === 'ascend'
                      ? h(icons.UpOutlined, { size: '0.6em' })
                      : h(icons.DownOutlined, { size: '0.6em' }),
                  ),
                col.sorter &&
                  currentSortField !== (col.columnKey || col.key || col.dataIndex) &&
                  h(
                    'span',
                    { className: 'ml-1 opacity-30' },
                    h(icons.SortAscendingOutlined, { size: '0.6em' }),
                  ),
              ),
            ),
          ),
        ),
        h(
          'tbody',
          null,
          paginatedData.length === 0
            ? h(
                'tr',
                null,
                h(
                  'td',
                  {
                    colSpan: columns.length + (rowSelection ? 1 : 0),
                    className: `${cellPadding} text-center text-dark-500 py-12`,
                  },
                  locale?.emptyText || h(Empty),
                ),
              )
            : paginatedData.map((record, rowIndex) => {
                const key = record[rowKey] || rowIndex;
                const isSelected = selectedKeys.includes(key);
                const rowProps = onRow ? onRow(record) : {};
                return h(
                  'tr',
                  {
                    key,
                    className: `border-t border-dark-700/50 hover:bg-dark-700/30 transition-colors ${isSelected ? 'bg-primary-600/10' : ''}`,
                    ...rowProps,
                  },
                  rowSelection &&
                    h(
                      'td',
                      { className: cellPadding },
                      h('input', {
                        type: 'checkbox',
                        className:
                          'rounded border-dark-600 bg-dark-800 text-primary-600 focus:ring-primary-500',
                        checked: isSelected,
                        onChange: (e) => {
                          let newKeys;
                          if (e.target.checked) {
                            newKeys = [...selectedKeys, key];
                          } else {
                            newKeys = selectedKeys.filter((k) => k !== key);
                          }
                          if (rowSelection.onChange) rowSelection.onChange(newKeys);
                        },
                      }),
                    ),
                  columns.map((col, colIndex) =>
                    h(
                      'td',
                      {
                        key: col.key || col.dataIndex || colIndex,
                        className: `${cellPadding} text-dark-200 ${col.ellipsis ? 'truncate max-w-0' : ''}`,
                        style: col.width ? { width: col.width, maxWidth: col.width } : undefined,
                      },
                      col.render
                        ? col.render(record[col.dataIndex], record, rowIndex)
                        : (record[col.dataIndex] ?? '-'),
                    ),
                  ),
                );
              }),
        ),
      ),
    ),
    renderPagination(),
  );
};

export { Table };
